(function () {
  'use strict';

  var STORAGE_KEY = 'qds_customer_region';
  var FIRST_VISIT_KEY = 'qds_pincode_collected';
  var POPUP_SEEN_KEY = 'qds_popup_seen';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  var GEO_API_URL = 'https://ipapi.co/json/';
  var PINCODE_API_URL = 'https://api.postalpincode.in/pincode/';
  var PINCODE_API_TIMEOUT_MS = 8000;
  var pincodeApiCache = {};

  var DELHI_NCR = 'Delhi NCR';
  var MUMBAI = 'Mumbai';
  var KOLKATA = 'Kolkata';
  var MOHALI = 'Mohali';
  var BENGALURU = 'Bengaluru';

  var CITY_TO_REGION = {
    delhi: DELHI_NCR,
    'new delhi': DELHI_NCR,
    noida: DELHI_NCR,
    'greater noida': DELHI_NCR,
    ghaziabad: DELHI_NCR,
    faridabad: DELHI_NCR,
    gurgaon: DELHI_NCR,
    gurugram: DELHI_NCR,
    mumbai: MUMBAI,
    thane: MUMBAI,
    navi: MUMBAI,
    kolkata: KOLKATA,
    howrah: KOLKATA,
    mohali: MOHALI,
    's.a.s. nagar': MOHALI,
    chandigarh: MOHALI,
    bengaluru: BENGALURU,
    bangalore: BENGALURU
  };

  /* First 3 digits → city (non–Delhi NCR). */
  var PINCODE_PREFIX_TO_REGION = {
    '400': MUMBAI,
    '401': MUMBAI,
    '402': MUMBAI,
    '403': MUMBAI,
    '410': MUMBAI,
    '411': MUMBAI,
    '421': MUMBAI,
    '700': KOLKATA,
    '701': KOLKATA,
    '702': KOLKATA,
    '703': KOLKATA,
    '711': KOLKATA,
    '712': KOLKATA,
    '743': KOLKATA,
    '140': MOHALI,
    '160': MOHALI,
    '560': BENGALURU,
    '561': BENGALURU,
    '562': BENGALURU
  };

  var DELHI_NCR_PINCODE_RANGES = [
    [110001, 110097],
    [121001, 121015],
    [122001, 122022],
    [122051, 122052],
    [122098, 122098],
    [122101, 122105],
    [122413, 122414],
    [201001, 201015],
    [201301, 201318]
  ];

  var REGION_MESSAGES = {};
  REGION_MESSAGES[DELHI_NCR] = 'Delivery within 2-3 hours';
  REGION_MESSAGES[MUMBAI] = 'Delivery in next 1 to 2 days';
  REGION_MESSAGES[KOLKATA] = 'Delivery in next 1 to 2 days';
  REGION_MESSAGES[MOHALI] = 'Delivery in next 1 to 2 days';
  REGION_MESSAGES[BENGALURU] = 'Delivery in next 1 to 2 days';

  var DEFAULT_MESSAGE = 'Quick Delivery';
  var CUTOFF_HOUR = 16; /* Delhi NCR 4:00 PM IST */

  var EMPTY_FLAGS = {
    delhi: false,
    mumbai: false,
    kidskolkata: false,
    quest_kolkata: false,
    mohali: false,
    bengaluru: false
  };

  var activeCustomerRegion = null;

  function normalizeCity(city) {
    return (city || '').trim().toLowerCase();
  }

  function mapCityToRegion(city) {
    return CITY_TO_REGION[normalizeCity(city)] || null;
  }

  function mapPincodeToRegion(pincode) {
    var digits = String(pincode || '').replace(/\D/g, '');
    if (!isValidIndianPincodeFormat(digits)) return null;
    if (isDelhiNcrPincode(digits)) return DELHI_NCR;
    return PINCODE_PREFIX_TO_REGION[digits.slice(0, 3)] || null;
  }

  function isValidIndianPincodeFormat(digits) {
    return /^[1-9][0-9]{5}$/.test(String(digits || ''));
  }

  function isDelhiNcrPincode(digits) {
    var n = parseInt(digits, 10);
    if (isNaN(n)) return false;
    for (var i = 0; i < DELHI_NCR_PINCODE_RANGES.length; i++) {
      var range = DELHI_NCR_PINCODE_RANGES[i];
      if (n >= range[0] && n <= range[1]) return true;
    }
    return false;
  }

  function isValidIndianPincode(digits) {
    return isValidIndianPincodeFormat(digits);
  }

  function parseEligible(value) {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    return false;
  }

  function parseCityFlags(raw) {
    if (!raw) return Object.assign({}, EMPTY_FLAGS);
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      /* Legacy: boolean true meant Delhi only */
      if (raw === true || raw.delhi !== undefined || raw.mumbai !== undefined) {
        if (raw === true) return Object.assign({}, EMPTY_FLAGS, { delhi: true });
        return Object.assign({}, EMPTY_FLAGS, {
          delhi: parseEligible(raw.delhi),
          mumbai: parseEligible(raw.mumbai),
          kidskolkata: parseEligible(raw.kidskolkata),
          quest_kolkata: parseEligible(raw.quest_kolkata),
          mohali: parseEligible(raw.mohali),
          bengaluru: parseEligible(raw.bengaluru)
        });
      }
    }
    if (raw === true || raw === 'true') {
      return Object.assign({}, EMPTY_FLAGS, { delhi: true });
    }
    try {
      if (typeof raw === 'string') {
        var parsed = JSON.parse(raw);
        return parseCityFlags(parsed);
      }
    } catch (e) {
      /* ignore */
    }
    return Object.assign({}, EMPTY_FLAGS);
  }

  function isExpressForRegion(flags, region) {
    if (!region || !flags) return false;
    if (region === DELHI_NCR) return !!flags.delhi;
    if (region === MUMBAI) return !!flags.mumbai;
    if (region === KOLKATA) return !!(flags.kidskolkata || flags.quest_kolkata);
    if (region === MOHALI) return !!flags.mohali;
    if (region === BENGALURU) return !!flags.bengaluru;
    return false;
  }

  function getRegionMessage(region) {
    return REGION_MESSAGES[region] || DEFAULT_MESSAGE;
  }

  function validatePincodeWithApi(digits) {
    if (Object.prototype.hasOwnProperty.call(pincodeApiCache, digits)) {
      return Promise.resolve(pincodeApiCache[digits]);
    }

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    if (controller) {
      timer = setTimeout(function () {
        controller.abort();
      }, PINCODE_API_TIMEOUT_MS);
    }

    /* Free API DB is incomplete (e.g. real Mumbai 400038 missing). Format-valid
       Indian pins still accepted when API has no record / is unreachable. */
    var formatFallback = {
      valid: true,
      source: 'format',
      district: '',
      state: ''
    };

    return fetch(PINCODE_API_URL + encodeURIComponent(digits), {
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Pincode API failed');
        return response.json();
      })
      .then(function (payload) {
        var row = Array.isArray(payload) ? payload[0] : null;
        var ok =
          row &&
          String(row.Status || '').toLowerCase() === 'success' &&
          Array.isArray(row.PostOffice) &&
          row.PostOffice.length > 0;
        var result = ok
          ? {
              valid: true,
              source: 'api',
              district: row.PostOffice[0].District || '',
              state: row.PostOffice[0].State || ''
            }
          : formatFallback;
        pincodeApiCache[digits] = result;
        return result;
      })
      .catch(function () {
        pincodeApiCache[digits] = formatFallback;
        return formatFallback;
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  function setPopupSubmitting(isSubmitting) {
    document.querySelectorAll('[data-qds-popup-submit]').forEach(function (button) {
      button.disabled = !!isSubmitting;
      if (!button.getAttribute('data-qds-label')) {
        button.setAttribute('data-qds-label', button.textContent || 'Check');
      }
      button.textContent = isSubmitting
        ? 'Checking…'
        : button.getAttribute('data-qds-label') || 'Check';
    });
    document.querySelectorAll('[data-qds-popup-input]').forEach(function (input) {
      input.disabled = !!isSubmitting;
    });
  }

  function getTestRegionOverride() {
    try {
      var params = new URLSearchParams(window.location.search);
      var region = params.get('qds_region');
      if (region) return { region: region, source: 'test', pincode: null };

      var pincode = params.get('qds_pincode');
      if (pincode) {
        return {
          region: mapPincodeToRegion(pincode),
          source: 'pincode',
          pincode: pincode.replace(/\D/g, '')
        };
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function readCachedData() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      var cached = JSON.parse(raw);
      if (!cached || !cached.expiresAt || Date.now() > cached.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return cached;
    } catch (e) {
      return null;
    }
  }

  function writeCachedData(data) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          region: data.region || null,
          source: data.source || 'geo',
          pincode: data.pincode || null,
          district: data.district || '',
          state: data.state || '',
          expiresAt: Date.now() + CACHE_TTL_MS
        })
      );
    } catch (e) {
      /* ignore storage errors */
    }
  }

  function markPincodeCollected() {
    try {
      localStorage.setItem(FIRST_VISIT_KEY, 'true');
    } catch (e) {
      /* ignore */
    }
    markPopupSeen();
  }

  function hasCollectedPincode() {
    try {
      return localStorage.getItem(FIRST_VISIT_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function markPopupSeen() {
    try {
      localStorage.setItem(POPUP_SEEN_KEY, 'true');
    } catch (e) {
      /* ignore */
    }
  }

  /* Auto-open at most once per browser. Close or pincode both count as seen. */
  function hasPopupSeen() {
    try {
      if (localStorage.getItem(POPUP_SEEN_KEY) === 'true') return true;
      /* Existing customers who already entered a pincode */
      if (localStorage.getItem(FIRST_VISIT_KEY) === 'true') return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  function fetchCustomerRegion() {
    var testOverride = getTestRegionOverride();
    if (testOverride) {
      writeCachedData(testOverride);
      return Promise.resolve(testOverride.region);
    }

    var cached = readCachedData();
    if (cached) {
      return Promise.resolve(cached.region || null);
    }

    return Promise.resolve(null);
  }

  function setBadgeVisibility(badge, visible, customerRegion) {
    badge.classList.toggle('is-visible', visible);
    badge.hidden = !visible;

    var messageEl = badge.querySelector('[data-quick-delivery-message]');
    if (messageEl && customerRegion) {
      messageEl.textContent = getRegionMessage(customerRegion);
    }
  }

  function getIstHour() {
    var now = new Date();
    var istOffsetMs = (5 * 60 + 30) * 60 * 1000;
    var ist = new Date(now.getTime() + istOffsetMs + now.getTimezoneOffset() * 60 * 1000);
    return ist.getHours();
  }

  function getVariantCityFlags(variantId) {
    var productData = document.getElementById('qds-product-data');
    if (productData && variantId) {
      try {
        var variants = JSON.parse(productData.textContent || '{}');
        var value = variants[variantId];
        if (value === undefined) value = variants[String(variantId)];
        if (value !== undefined) return parseCityFlags(value);
      } catch (e) {
        /* fall through */
      }
    }

    var panel = document.querySelector('[data-qds-pdp-panel]');
    if (panel) {
      var fromPanel = panel.getAttribute('data-qds-cities');
      if (fromPanel) return parseCityFlags(fromPanel);
    }

    return Object.assign({}, EMPTY_FLAGS);
  }

  function getBadgeCityFlags(badge) {
    var attr = badge.getAttribute('data-qds-cities');
    if (attr) return parseCityFlags(attr);
    if (parseEligible(badge.getAttribute('data-quick-delivery-eligible'))) {
      return Object.assign({}, EMPTY_FLAGS, { delhi: true });
    }
    return Object.assign({}, EMPTY_FLAGS);
  }

  function isFw26Product() {
    var panel = document.querySelector('[data-qds-pdp-panel]');
    return !!(panel && panel.getAttribute('data-qds-fw26') === 'true');
  }

  function updateExpressCopy(region) {
    var cutoffEl = document.querySelector('[data-qds-cutoff]');
    var subtitleEl = document.querySelector('[data-qds-subtitle]');
    var urgentWrap = document.querySelector('[data-qds-urgent-wrap]');
    var hour = getIstHour();

    /* FW26: warehouse transfer delay — never promise same-day / 2-3 hours */
    if (isFw26Product()) {
      if (cutoffEl) {
        var cutoffItem = cutoffEl.closest('li');
        if (cutoffItem) cutoffItem.hidden = true;
        else cutoffEl.hidden = true;
      }
      if (subtitleEl) subtitleEl.textContent = 'Get it in 5 to 7 days';
      if (urgentWrap) urgentWrap.hidden = true;
      resetUrgentCheck();
      return;
    }

    if (cutoffEl) {
      var cutoffRow = cutoffEl.closest('li');
      if (cutoffRow) cutoffRow.hidden = false;
      cutoffEl.hidden = false;
    }
    if (urgentWrap) urgentWrap.hidden = false;

    if (region === DELHI_NCR) {
      if (cutoffEl) {
        var label = hour >= CUTOFF_HOUR ? '10:00 AM tomorrow' : '4:00 PM';
        cutoffEl.innerHTML = 'Place order by <strong>' + label + '</strong>';
      }
      if (subtitleEl) {
        subtitleEl.textContent =
          hour >= CUTOFF_HOUR
            ? 'Order now for next-day quick delivery'
            : 'Get it in 2-3 hours or same day';
      }
      return;
    }

    if (cutoffEl) {
      cutoffEl.innerHTML = 'Place order by <strong>4:00 PM</strong>';
    }
    if (subtitleEl) {
      subtitleEl.textContent = 'Next 1 to 2 days';
    }
  }

  function getUrgentCheck() {
    return document.querySelector('[data-qds-urgent-check]');
  }

  function isUrgentRequested() {
    var check = getUrgentCheck();
    var quick = document.querySelector('[data-qds-pdp-delivery]');
    return !!(check && check.checked && quick && !quick.hidden);
  }

  function buildUrgentAttributes(enabled) {
    var cached = readCachedData() || {};
    if (!enabled) {
      return {
        'Urgent Delivery': '',
        'Porter Charge Accepted': '',
        'Express City': '',
        'Delivery Pincode': ''
      };
    }
    return {
      'Urgent Delivery': 'Yes',
      'Porter Charge Accepted': 'Yes',
      'Express City': cached.region || activeCustomerRegion || '',
      'Delivery Pincode': cached.pincode || ''
    };
  }

  function syncUrgentCartAttributes() {
    var attrs = buildUrgentAttributes(isUrgentRequested());
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {
      /* non-blocking — ATC can still continue */
    });
  }

  function resetUrgentCheck() {
    var check = getUrgentCheck();
    if (check) check.checked = false;
    syncUrgentCartAttributes();
  }

  function updateBadgeElement(badge, customerRegion) {
    var flags = getBadgeCityFlags(badge);
    var visible = isExpressForRegion(flags, customerRegion);
    setBadgeVisibility(badge, visible, visible ? customerRegion : null);
  }

  function showPdpResult(mode, pincode, region) {
    var panel = document.querySelector('[data-qds-pdp-panel]');
    if (!panel) return;

    var result = panel.querySelector('[data-qds-pdp-result]');
    var quick = panel.querySelector('[data-qds-pdp-delivery]');
    var standard = panel.querySelector('[data-qds-pdp-standard]');

    if (!result) return;

    result.hidden = false;

    if (mode === 'quick') {
      if (quick) quick.hidden = false;
      if (standard) standard.hidden = true;
      updateExpressCopy(region);
    } else {
      if (quick) quick.hidden = true;
      if (standard) standard.hidden = false;
      resetUrgentCheck();
    }
  }

  function hidePdpResult() {
    var panel = document.querySelector('[data-qds-pdp-panel]');
    if (!panel) return;
    var result = panel.querySelector('[data-qds-pdp-result]');
    var quick = panel.querySelector('[data-qds-pdp-delivery]');
    var standard = panel.querySelector('[data-qds-pdp-standard]');
    if (result) result.hidden = true;
    if (quick) quick.hidden = true;
    if (standard) standard.hidden = true;
    resetUrgentCheck();
  }

  function renderPdpForRegion(region, pincode) {
    /* FW26 live stock is often at another warehouse — show 5-7 days, not express */
    if (isFw26Product()) {
      showPdpResult('standard', pincode, region);
      return;
    }

    var flags = getVariantCityFlags(getSelectedVariantId());
    if (isExpressForRegion(flags, region)) {
      showPdpResult('quick', pincode, region);
      return;
    }
    showPdpResult('standard', pincode, region);
  }

  function updatePdpPanel(_customerRegion, variantId) {
    var panel = document.querySelector('[data-qds-pdp-panel]');
    if (!panel) return;

    variantId = variantId || getSelectedVariantId();
    var flags = getVariantCityFlags(variantId);
    var anyEligible =
      flags.delhi ||
      flags.mumbai ||
      flags.kidskolkata ||
      flags.quest_kolkata ||
      flags.mohali ||
      flags.bengaluru;
    panel.setAttribute('data-quick-delivery-eligible', anyEligible ? 'true' : 'false');
    panel.setAttribute('data-qds-cities', JSON.stringify(flags));
    panel.hidden = false;

    var cached = readCachedData();
    if (cached && cached.source === 'pincode' && cached.pincode && isValidIndianPincode(cached.pincode)) {
      renderPdpForRegion(cached.region, cached.pincode);
      return;
    }

    hidePdpResult();
  }

  function updateAllBadges(customerRegion) {
    activeCustomerRegion = customerRegion;
    document.querySelectorAll('[data-quick-delivery-badge]').forEach(function (badge) {
      if (badge.closest('[data-qds-pdp-panel]')) return;
      updateBadgeElement(badge, customerRegion);
    });

    if (document.getElementById('qds-product-data') || document.querySelector('[data-qds-pdp-panel]')) {
      updatePdpPanel(customerRegion, getSelectedVariantId());
    }

    syncPincodeUi();
  }

  function updateProductPageBadge(customerRegion, variantId) {
    updatePdpPanel(customerRegion, variantId);
  }

  function getSelectedVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? input.value : null;
  }

  function bindVariantChanges() {
    if (typeof jQuery !== 'undefined') {
      jQuery(document).on('variant:changed', function (_evt, variant) {
        if (variant && variant.id) {
          updateProductPageBadge(activeCustomerRegion, variant.id);
        }
      });
    }

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (target && target.name === 'id') {
        updateProductPageBadge(activeCustomerRegion, target.value);
      }
    });
  }

  function setPincodeStatus(message, type) {
    document.querySelectorAll('[data-qds-pincode-status]').forEach(function (el) {
      if (!message) {
        el.hidden = true;
        el.textContent = '';
        el.className = 't4s-qds-pdp__pincode-status';
        return;
      }

      el.hidden = false;
      el.textContent = message;
      el.className =
        't4s-qds-pdp__pincode-status t4s-qds-pdp__pincode-status--' + (type || 'info');
    });
  }

  function setPopupStatus(message) {
    document.querySelectorAll('[data-qds-popup-status]').forEach(function (el) {
      if (!message) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      el.hidden = false;
      el.textContent = message;
    });
  }

  function shippingPlace(cached) {
    if (!cached) return '';
    if (cached.district) return cached.district + ', India';
    if (cached.region) return cached.region + ', India';
    return '';
  }

  function syncSavedPincodeText() {
    var cached = readCachedData();
    var hasSavedPincode =
      cached && cached.source === 'pincode' && cached.pincode && isValidIndianPincode(cached.pincode);
    var place = shippingPlace(cached);

    document.querySelectorAll('[data-qds-pdp-pincode]').forEach(function (wrap) {
      wrap.classList.toggle('t4s-qds-pdp__pincode--saved', hasSavedPincode);
      wrap.classList.toggle('t4s-qds-pdp__pincode--check', !hasSavedPincode);
    });

    document.querySelectorAll('[data-qds-options-for]').forEach(function (el) {
      el.hidden = !hasSavedPincode;
    });

    document.querySelectorAll('[data-qds-pincode-desc]').forEach(function (el) {
      el.hidden = hasSavedPincode;
    });

    document.querySelectorAll('[data-qds-current-pincode]').forEach(function (el) {
      if (hasSavedPincode) {
        el.hidden = false;
        el.textContent = cached.pincode;
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });

    document.querySelectorAll('[data-qds-shipping]').forEach(function (el) {
      if (hasSavedPincode && place) {
        el.hidden = false;
        el.innerHTML = 'Shipping to: <strong>' + place + '</strong>';
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });

    document.querySelectorAll('[data-qds-open-popup]').forEach(function (button) {
      if (hasSavedPincode) {
        button.textContent = 'Change';
        button.setAttribute('data-qds-popup-action', 'change');
        button.classList.remove('t4s-qds-pdp__action-btn--enter');
        button.classList.add('t4s-qds-pdp__action-btn--change');
      } else {
        button.textContent = 'Enter Pincode';
        button.setAttribute('data-qds-popup-action', 'check');
        button.classList.add('t4s-qds-pdp__action-btn--enter');
        button.classList.remove('t4s-qds-pdp__action-btn--change');
      }
    });
  }

  function syncPincodeUi() {
    var cached = readCachedData();
    syncSavedPincodeText();
    document.querySelectorAll('[data-qds-popup-input]').forEach(function (input) {
      if (cached && cached.source === 'pincode' && cached.pincode && isValidIndianPincode(cached.pincode)) {
        input.value = cached.pincode;
      }
    });
  }

  function commitPincode(digits, place) {
    setPincodeStatus('');
    setPopupStatus('');
    var region = mapPincodeToRegion(digits);
    place = place || {};
    writeCachedData({
      region: region,
      source: 'pincode',
      pincode: digits,
      district: place.district || '',
      state: place.state || ''
    });
    markPincodeCollected();
    activeCustomerRegion = region;
    syncSavedPincodeText();

    document.querySelectorAll('[data-quick-delivery-badge]').forEach(function (badge) {
      if (badge.closest('[data-qds-pdp-panel]')) return;
      updateBadgeElement(badge, region);
    });

    renderPdpForRegion(region, digits);
    closePincodePopup();
  }

  function openPincodePopup() {
    document.querySelectorAll('[data-qds-popup]').forEach(function (el) {
      el.hidden = false;
    });
    syncPincodeUi();
  }

  function closePincodePopup() {
    markPopupSeen();
    document.querySelectorAll('[data-qds-popup]').forEach(function (el) {
      el.hidden = true;
    });
  }

  function handlePopupSubmit() {
    var input = document.querySelector('[data-qds-popup-input]');
    var value = input ? input.value : '';
    var digits = String(value || '').replace(/\D/g, '');

    if (!isValidIndianPincode(digits)) {
      setPopupStatus('Please enter a valid 6-digit Indian pincode.');
      return;
    }

    setPopupStatus('Verifying pincode…');
    setPopupSubmitting(true);

    validatePincodeWithApi(digits)
      .then(function (result) {
        if (!result || !result.valid) {
          setPopupStatus('Please enter a valid 6-digit Indian pincode.');
          hidePdpResult();
          return;
        }
        commitPincode(digits, result);
      })
      .catch(function () {
        /* Format already validated — allow through if API path throws unexpectedly */
        commitPincode(digits);
      })
      .finally(function () {
        setPopupSubmitting(false);
      });
  }

  function bindUrgentCheck() {
    document.querySelectorAll('[data-qds-urgent-check]').forEach(function (input) {
      input.addEventListener('change', function () {
        syncUrgentCartAttributes();
      });
    });

    /* Sync attributes right before ATC so order gets cart attrs */
    document.addEventListener(
      'click',
      function (event) {
        var btn = event.target.closest('[data-atc-form], form[action*="/cart/add"] [type="submit"]');
        if (!btn) return;
        syncUrgentCartAttributes();
      },
      true
    );

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
        syncUrgentCartAttributes();
      },
      true
    );
  }

  function bindPopup() {
    document.querySelectorAll('[data-qds-open-popup]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        openPincodePopup();
      });
    });

    document.querySelectorAll('[data-qds-popup-submit]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        handlePopupSubmit();
      });
    });

    document.querySelectorAll('[data-qds-popup-input]').forEach(function (input) {
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          handlePopupSubmit();
        }
      });
    });

    document.querySelectorAll('[data-qds-popup-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closePincodePopup();
      });
    });
  }

  function init() {
    bindPopup();
    bindUrgentCheck();

    fetchCustomerRegion().then(function (customerRegion) {
      updateAllBadges(customerRegion);
      syncPincodeUi();

      if (document.getElementById('qds-product-data')) {
        updateProductPageBadge(activeCustomerRegion, getSelectedVariantId());
        bindVariantChanges();
      }

      if (!hasPopupSeen()) {
        openPincodePopup();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
